/**
 * Utility functions for obfuscating sensitive customer information
 * in the Simple Scheduler UI
 */

/**
 * Masks a name by showing first 2 letters and last letter of each part
 * @example "John Smith" -> "Jo*n Sm**h"
 */
export const maskName = (name: string): string => {
  if (!name) return '';

  const parts = name.trim().split(' ');
  return parts
    .map((part) => {
      if (part.length === 0) return '';
      if (part.length <= 2) return part;
      if (part.length === 3) return part[0] + '*' + part[2];

      // Show first 2 chars and last char
      const firstTwo = part.slice(0, 2);
      const lastChar = part.slice(-1);
      const middleLength = Math.max(1, part.length - 3);

      return firstTwo + '*'.repeat(middleLength) + lastChar;
    })
    .join(' ');
};

/**
 * Masks a phone number by showing area code and last 4 digits
 * @example "(555) 123-4567" -> "(555) ***-4567"
 */
export const maskPhone = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-digits
  const digitsOnly = phone.replace(/\D/g, '');

  if (digitsOnly.length < 7) return '*'.repeat(phone.length);

  // Get the area code (first 3) and last 4 digits
  const areaCode = digitsOnly.slice(0, 3);
  const lastFour = digitsOnly.slice(-4);

  // Format as (XXX) ***-XXXX
  if (digitsOnly.length === 10) {
    return `(${areaCode}) ***-${lastFour}`;
  }

  // For other formats, show first 3 and last 4
  if (phone.includes('(')) {
    return phone.replace(/\d/g, (digit, index) => {
      const digitIndex =
        phone.slice(0, index + 1).replace(/\D/g, '').length - 1;
      if (digitIndex < 3 || digitIndex >= digitsOnly.length - 4) {
        return digit;
      }
      return '*';
    });
  }

  return phone.replace(/\d/g, '*');
};

/**
 * Masks an email address by showing first 3 chars and domain
 * @example "john.doe@example.com" -> "joh****@example.com"
 */
export const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return email;

  const [localPart, domain] = email.split('@');

  if (localPart.length <= 3) {
    return `${localPart}@${domain}`;
  }

  const visibleChars = 3;
  const maskedLength = Math.max(4, localPart.length - visibleChars);

  return `${localPart.slice(0, visibleChars)}${'*'.repeat(maskedLength)}@${domain}`;
};

/**
 * Masks an address by showing house number, partial street name, and full city/state/zip
 * @example "123 Main Street, Apt 4B, New York, NY 10001" -> "123 Ma** ******, Apt 4B, New York, NY 10001"
 */
export const maskAddress = (address: string): string => {
  if (!address) return '';

  // Split by comma to get address parts
  const parts = address.split(',').map((part) => part.trim());

  if (parts.length < 3) {
    // If we can't parse properly, just mask middle portions of words
    return address
      .split(' ')
      .map((word, index) => {
        // Keep first word (likely house number) and last 2 words (likely state zip)
        if (index === 0 || word.match(/^\d+$/)) return word;
        if (word.length <= 3) return word;

        // For longer words, show first 2 chars
        return word.slice(0, 2) + '*'.repeat(word.length - 2);
      })
      .join(' ');
  }

  // Process street address (first part)
  const streetParts = parts[0].split(' ');
  const maskedStreet = streetParts
    .map((word, index) => {
      // Keep house numbers and unit indicators
      if (
        word.match(/^\d+$/) ||
        word.toLowerCase() === 'apt' ||
        word.toLowerCase() === 'unit'
      ) {
        return word;
      }
      // Keep short words (like "St", "Ave", etc)
      if (word.length <= 3) return word;
      // Mask longer words but show first 2 letters
      return word.slice(0, 2) + '*'.repeat(word.length - 2);
    })
    .join(' ');

  // Keep apartment/unit info if it exists (second part) and city/state/zip as is
  const maskedParts = [maskedStreet, ...parts.slice(1)];

  return maskedParts.join(', ');
};

/**
 * Masks first and last name separately with less aggressive masking
 */
export const maskFirstName = (firstName: string): string => {
  if (!firstName) return '';
  if (firstName.length <= 2) return firstName;
  if (firstName.length === 3) return firstName[0] + '*' + firstName[2];

  // Show first 2 chars and last char
  return (
    firstName.slice(0, 2) +
    '*'.repeat(firstName.length - 3) +
    firstName.slice(-1)
  );
};

export const maskLastName = (lastName: string): string => {
  if (!lastName) return '';
  if (lastName.length <= 2) return lastName;
  if (lastName.length === 3) return lastName[0] + '*' + lastName[2];

  // Show first 2 chars and last char
  return (
    lastName.slice(0, 2) + '*'.repeat(lastName.length - 3) + lastName.slice(-1)
  );
};
